import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { getAuth, updatePassword } from 'firebase/auth';
import { useAuth, useLogout, useLoginMethod } from 'hooks';
import styles from './Profile.module.scss';
import { activateSnackbar } from 'store/features/ui';
import { ColorThemeSelector } from './ColorThemeSelector/ColorThemeSelector';

export function Profile() {
    const authentication = useAuth();
    const logout = useLogout();
    const loginMethod = useLoginMethod();
    const dispatch = useDispatch();
    const auth = getAuth();
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
        useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordAgain, setNewPasswordAgain] = useState('');
    const launchChangePasswordModal = () => {
        setIsChangePasswordModalOpen(true);
    };
    const resetPasswordModal = () => {
        setIsChangePasswordModalOpen(false);
        setNewPassword('');
        setNewPasswordAgain('');
    };
    const handleChangePassword = () => {
        if (newPassword !== newPasswordAgain) {
            dispatch(
                activateSnackbar({
                    message: 'Passwords do not match',
                    severity: 'error',
                })
            );
        } else if (!!auth.currentUser) {
            updatePassword(auth.currentUser, newPassword)
                .then(() => {
                    dispatch(
                        activateSnackbar({
                            message: 'Password changed successfully',
                            severity: 'success',
                        })
                    );
                    resetPasswordModal();
                })
                .catch(() => {
                    resetPasswordModal();
                });
        } else {
            dispatch(
                activateSnackbar({
                    message: 'User not logged in.',
                    severity: 'error',
                })
            );
            resetPasswordModal();
        }
    };
    const cardStyles = {
        marginBottom: '10px',
    };

    return (
        <div data-testid="Profile" className={styles.container}>
            <Card sx={cardStyles}>
                <Typography variant="h4">
                    {!!authentication?.user?.displayName && (
                        <>Welcome, {authentication.user.displayName}</>
                    )}
                    {!!authentication?.user &&
                        !authentication?.user?.displayName &&
                        !!authentication?.user?.email && (
                            <>Welcome, {authentication.user.email}</>
                        )}
                </Typography>
                <CardContent>
                    {!!authentication.user && (
                        <div>
                            <div>
                                You are currently logged in via {loginMethod()}.
                            </div>
                            <div className={styles.logoutButton}>
                                <Button
                                    variant="contained"
                                    onClick={launchChangePasswordModal}
                                    sx={{ marginRight: '5px' }}
                                >
                                    Change Password
                                </Button>
                                <Button variant="contained" onClick={logout}>
                                    Logout
                                </Button>
                            </div>
                        </div>
                    )}
                    {!authentication.user && (
                        <div>
                            <h1>Welcome</h1>
                            We&#39;re not sure how you&#39;re on this page right
                            now, but please <a href="/">navigate here</a> to log
                            in, or the application will not work correctly.
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card sx={cardStyles}>
                <CardHeader>User Preferences</CardHeader>
                <CardContent>
                    <ColorThemeSelector />
                </CardContent>
            </Card>
            <Dialog
                open={isChangePasswordModalOpen}
                onClose={() => setIsChangePasswordModalOpen(false)}
            >
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Use the fields below to change your password:
                    </DialogContentText>
                    <TextField
                        fullWidth
                        label="New Password"
                        type="password"
                        sx={{ marginTop: '10px' }}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="New Password (again)"
                        type="password"
                        sx={{ marginTop: '10px' }}
                        value={newPasswordAgain}
                        onChange={(e) => setNewPasswordAgain(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsChangePasswordModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleChangePassword}>Change</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
